import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { updateError } from './user';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { isQuickReplyTemplate } from '../../components/whatsappComponents/quickReplyUtils';
import { whatsappTemplateIdFromStep } from '../../utils/campaignTemplateApproval';
import { getConnectedWhatsappBusinessAccounts } from '../../utils/whatsappAccountLifecycle';

// -----------------------------------------------

const initialState = {
  practiceOwnerId: null,
  requestGeneration: 0,
  activeStep: 0,
  campaignData: {
    name: '',
    channel: 'EMAIL',
    campaignType: 'STANDARD',
    templateId: null,
    whatsappTemplateId: null,
    whatsappPhoneId: null,
    audienceId: null,
    recipientFilters: null,
    recipientArchiveIncluded: false,
    scheduledAt: null,
    sendNow: true,
    tags: [],
    additionalContext: {},
    sequenceMessages: [],
    practiceSequenceId: null,
    // Typed engagement follow-up source (see CampaignEngagementSource on the
    // backend). null for a normal campaign; when set, the audience step
    // renders a locked source card instead of the audience/build tabs.
    // Shape: { sourceCampaignId, rule, excludeBooked, sourceCampaignName }.
    engagementSource: null,
  },
  // UI state
  isLoading: false,
  isSaving: false,
  isSubmitting: false,
  submitSuccess: false,
  submitError: null,
  launchedCampaignId: null,
  templateSearch: '',
  templateCategory: 'ALL',
  audienceTab: 'existing',
  // Data fetched from API
  templates: [],
  templatesLoading: false,
  audiences: [],
  audiencesLoading: false,
  whatsappTemplates: [],
  whatsappTemplatesLoading: false,
  whatsappPhones: [],
  whatsappPhonesLoading: false,
  recipientCount: null,
  recipientCountLoading: false,
  whatsappLimits: null,
  sequenceTemplates: [],
  sequenceTemplatesLoading: false,
  // Engagement follow-up: source-campaign analysis (rule counts + tracking
  // caveat) and live preview (paginated matching leads). Both are keyed to
  // whichever engagement source campaign was last requested — the dialog and
  // the audience step each request afresh when they need current data.
  engagementAnalysis: null,
  engagementAnalysisLoading: false,
  // Scope of the campaign the current engagementAnalysis belongs to — lets
  // consumers (CampaignReportPage's "Create follow-up" gate) tell a fresh
  // analysis apart from a stale one left over from a previously viewed
  // campaign after an in-place report -> report navigation.
  engagementAnalysisCampaignId: null,
  // Set when the last fetchEngagementAnalysis call failed, so callers can
  // show "couldn't load" instead of misreading a null analysis as "zero
  // eligible recipients".
  engagementAnalysisError: false,
  engagementPreview: null,
  engagementPreviewLoading: false,
  // Edit mode
  editCampaignId: null,
  editCampaignLoading: false,
};

let builderPracticeOwnerId = null;
let builderRequestGeneration = 0;

const normalizePracticeId = (practiceId) => (
  practiceId === null || practiceId === undefined || practiceId === ''
    ? null
    : String(practiceId)
);

const freshBuilderState = (practiceOwnerId = null, requestGeneration = 0) => ({
  ...JSON.parse(JSON.stringify(initialState)),
  practiceOwnerId,
  requestGeneration,
});

const captureBuilderRequest = (practiceId) => {
  const practiceOwnerId = normalizePracticeId(practiceId);
  const requestGeneration = builderRequestGeneration;
  const isCurrent = () => practiceOwnerId === builderPracticeOwnerId
    && requestGeneration === builderRequestGeneration;
  return {
    isCurrent,
    commit(action) {
      if (isCurrent()) dispatch(action);
    },
  };
};

const slice = createSlice({
  name: 'campaignBuilder',
  initialState,
  reducers: {
    resetBuilder(state, action) {
      // Deep-clone initialState. Object.assign(state, initialState) copies
      // nested object/array REFERENCES (campaignData, tags, sequenceMessages,
      // additionalContext), so later reducers would mutate the shared
      // module-level initialState and pollute the next builder session.
      return freshBuilderState(null, action.payload.requestGeneration);
    },
    bindBuilderPractice(state, action) {
      const { practiceOwnerId, requestGeneration } = action.payload;
      if (
        state.practiceOwnerId === practiceOwnerId
        && state.requestGeneration === requestGeneration
      ) return undefined;
      return freshBuilderState(practiceOwnerId, requestGeneration);
    },
    setActiveStep(state, action) {
      state.activeStep = action.payload;
      // Clear any stale submit error when navigating between steps, so a prior
      // failed launch doesn't keep showing its error banner after the user goes
      // back to fix something.
      state.submitError = null;
    },
    updateCampaignData(state, action) {
      state.campaignData = {
        ...state.campaignData,
        ...action.payload,
      };
    },
    setTemplateSearch(state, action) {
      state.templateSearch = action.payload;
    },
    setTemplateCategory(state, action) {
      state.templateCategory = action.payload;
    },
    setAudienceTab(state, action) {
      state.audienceTab = action.payload;
    },
    setEditCampaign(state, action) {
      const campaign = action.payload;
      state.editCampaignId = campaign.id;
      state.campaignData = {
        name: campaign.name || '',
        channel: campaign.channel || 'EMAIL',
        campaignType: campaign.campaign_type || 'STANDARD',
        templateId: campaign.engagement_template?.id || campaign.engagement_template || null,
        whatsappTemplateId: campaign.whatsapp_template?.id || campaign.whatsapp_template || null,
        whatsappPhoneId: campaign.whatsapp_phone?.id || campaign.whatsapp_phone || null,
        audienceId: campaign.audience?.audience?.id || campaign.audience?.audience || null,
        recipientFilters: campaign.recepient?.filters || null,
        recipientArchiveIncluded: !!campaign.recepient?.is_archived_included,
        scheduledAt: campaign.scheduled_at || null,
        sendNow: !campaign.scheduled_at,
        tags: campaign.tags || [],
        additionalContext: campaign.additional_context || {},
        sequenceMessages: [],
        practiceSequenceId: campaign.practice_sequence?.id || campaign.practice_sequence || null,
        // Read shape from the backend: { source_campaign: { id, name,
        // channel }, rule, exclude_booked }. Map to the same camelCase shape
        // the dialog and audience step write, so a re-opened follow-up draft
        // round-trips. channel: V1.1 locks the follow-up to the source
        // campaign's channel — prefer the nested source_campaign.channel
        // (survives even if this draft's own channel were ever edited), else
        // fall back to this campaign's own channel (pre-V1.1 drafts / the
        // source_campaign coming through as a bare id).
        engagementSource: campaign.engagement_source
          ? {
            sourceCampaignId: campaign.engagement_source.source_campaign?.id
              ?? campaign.engagement_source.source_campaign,
            sourceCampaignName: campaign.engagement_source.source_campaign?.name
              ?? campaign.engagement_source.source_campaign_name ?? '',
            channel: campaign.engagement_source.source_campaign?.channel
              ?? campaign.channel,
            rule: campaign.engagement_source.rule,
            excludeBooked: !!campaign.engagement_source.exclude_booked,
          }
          : null,
      };
      state.editCampaignLoading = false;
    },
    addSequenceMessage(state) {
      const msgs = state.campaignData.sequenceMessages;
      // Guard the previous delay with `|| 0` — a message imported via
      // apply-template can carry an undefined delay, and `undefined + 604800`
      // is NaN, which then poisons every subsequent message's delay.
      const lastDelay = msgs.length > 0 ? (msgs[msgs.length - 1].delay || 0) + 604800 : 0;
      msgs.push({
        localId: `msg_${Date.now()}_${msgs.length}`,
        subject: '',
        body: '',
        delay: lastDelay,
        channel: state.campaignData.channel || 'EMAIL',
        use_unlayer: true,
        unlayer_template: {},
        unlayer_html: '',
        is_enabled: true,
        whatsapp_template: null,
      });
    },
    removeSequenceMessage(state, action) {
      state.campaignData.sequenceMessages = state.campaignData.sequenceMessages.filter(
        (m) => m.localId !== action.payload
      );
    },
    updateSequenceMessage(state, action) {
      const { localId, ...updates } = action.payload;
      const msg = state.campaignData.sequenceMessages.find((m) => m.localId === localId);
      if (msg) {
        Object.assign(msg, updates);
      }
    },
    reorderSequenceMessages(state, action) {
      const reordered = action.payload || [];
      // The first message always sends at campaign start (delay 0) — both the
      // card label ("Sends at campaign start") and the message dialog
      // (isFirstMessage ? 0) enforce this. After a drag-reorder the new first
      // message could carry a stale non-zero delay, which would mislabel the
      // card AND ship the wrong delay to the backend. Normalise it to 0.
      if (reordered.length > 0) {
        reordered[0] = { ...reordered[0], delay: 0 };
      }
      state.campaignData.sequenceMessages = reordered;
    },
    // Loading states
    setTemplatesLoading(state, action) {
      state.templatesLoading = action.payload;
    },
    setTemplates(state, action) {
      state.templates = action.payload;
      state.templatesLoading = false;
    },
    setAudiencesLoading(state, action) {
      state.audiencesLoading = action.payload;
    },
    setAudiences(state, action) {
      state.audiences = action.payload;
      state.audiencesLoading = false;
    },
    setWhatsappTemplatesLoading(state, action) {
      state.whatsappTemplatesLoading = action.payload;
    },
    setWhatsappTemplates(state, action) {
      state.whatsappTemplates = action.payload;
      state.whatsappTemplatesLoading = false;
    },
    setWhatsappPhonesLoading(state, action) {
      state.whatsappPhonesLoading = action.payload;
    },
    setWhatsappPhones(state, action) {
      state.whatsappPhones = action.payload;
      state.whatsappPhonesLoading = false;
    },
    setRecipientCount(state, action) {
      state.recipientCount = action.payload;
      state.recipientCountLoading = false;
    },
    setRecipientCountLoading(state, action) {
      state.recipientCountLoading = action.payload;
    },
    setIsSubmitting(state, action) {
      state.isSubmitting = action.payload;
    },
    setIsSaving(state, action) {
      state.isSaving = action.payload;
    },
    setSubmitSuccess(state, action) {
      state.submitSuccess = action.payload;
    },
    setLaunchedCampaignId(state, action) {
      state.launchedCampaignId = action.payload;
    },
    setSubmitError(state, action) {
      state.submitError = action.payload;
    },
    setEditCampaignLoading(state, action) {
      state.editCampaignLoading = action.payload;
    },
    setWhatsappLimits(state, action) {
      state.whatsappLimits = action.payload;
    },
    setSequenceTemplatesLoading(state, action) {
      state.sequenceTemplatesLoading = action.payload;
    },
    setSequenceTemplates(state, action) {
      state.sequenceTemplates = action.payload;
      state.sequenceTemplatesLoading = false;
    },
    setEngagementAnalysisLoading(state, action) {
      state.engagementAnalysisLoading = action.payload;
    },
    setEngagementAnalysis(state, action) {
      const { data, campaignId } = action.payload;
      state.engagementAnalysis = data;
      state.engagementAnalysisCampaignId = campaignId;
      state.engagementAnalysisError = false;
      state.engagementAnalysisLoading = false;
    },
    setEngagementAnalysisError(state, action) {
      const { campaignId } = action.payload;
      state.engagementAnalysis = null;
      state.engagementAnalysisCampaignId = campaignId;
      state.engagementAnalysisError = true;
      state.engagementAnalysisLoading = false;
    },
    setEngagementPreviewLoading(state, action) {
      state.engagementPreviewLoading = action.payload;
    },
    setEngagementPreview(state, action) {
      state.engagementPreview = action.payload;
      state.engagementPreviewLoading = false;
    },
  },
});

export default slice.reducer;

export const {
  setActiveStep,
  updateCampaignData,
  setTemplateSearch,
  setTemplateCategory,
  setAudienceTab,
  setEditCampaign,
  addSequenceMessage,
  removeSequenceMessage,
  updateSequenceMessage,
  reorderSequenceMessages,
} = slice.actions;

export function bindBuilderPractice(practiceId) {
  const practiceOwnerId = normalizePracticeId(practiceId);
  if (practiceOwnerId !== builderPracticeOwnerId) {
    builderPracticeOwnerId = practiceOwnerId;
    builderRequestGeneration += 1;
  }
  return slice.actions.bindBuilderPractice({
    practiceOwnerId,
    requestGeneration: builderRequestGeneration,
  });
}

export function resetBuilder() {
  builderPracticeOwnerId = null;
  builderRequestGeneration += 1;
  return slice.actions.resetBuilder({ requestGeneration: builderRequestGeneration });
}

// ---- Thunks ----

export function fetchTemplateGallery(practiceId) {
  return async () => {
    if (!practiceId) return;
    const request = captureBuilderRequest(practiceId);
    if (!request.isCurrent()) return;
    try {
      request.commit(slice.actions.setTemplatesLoading(true));
      const response = await callApi(
        `/api/v1/practices/${practiceId}/template-gallery/?ordering=-created_at&limit=100`
      );
      if (response.status === 200) {
        request.commit(slice.actions.setTemplates(response.data?.results || []));
      } else {
        request.commit(slice.actions.setTemplates([]));
        request.commit(updateError({ success: false, message: 'Failed to load templates' }));
      }
    } catch (error) {
      request.commit(slice.actions.setTemplates([]));
      request.commit(updateError({ success: false, message: error.message || 'Failed to load templates' }));
    } finally {
      // Guarantee the spinner clears even on an early throw.
      request.commit(slice.actions.setTemplatesLoading(false));
    }
  };
}

export function fetchAudiences(practiceId) {
  return async () => {
    if (!practiceId) return;
    const request = captureBuilderRequest(practiceId);
    if (!request.isCurrent()) return;
    try {
      request.commit(slice.actions.setAudiencesLoading(true));
      const response = await callApi(
        `/api/v1/practices/${practiceId}/audiences/?ordering=-created_at&limit=100&fields={recepient_count,audience_internal_queryset,description,id,name,type,created_at}`
      );
      if (response.status === 200) {
        request.commit(slice.actions.setAudiences(response.data?.results || []));
      } else {
        request.commit(slice.actions.setAudiences([]));
        request.commit(updateError({ success: false, message: 'Failed to load audiences' }));
      }
    } catch (error) {
      request.commit(slice.actions.setAudiences([]));
      request.commit(updateError({ success: false, message: error.message || 'Failed to load audiences' }));
    } finally {
      // Guarantee the spinner clears even on an early throw.
      request.commit(slice.actions.setAudiencesLoading(false));
    }
  };
}

export function fetchWhatsappTemplates(practiceId) {
  return async () => {
    if (!practiceId) return;
    if (!hasFeatureAccess('whatsapp-messaging')) return;
    const request = captureBuilderRequest(practiceId);
    if (!request.isCurrent()) return;
    try {
      request.commit(slice.actions.setWhatsappTemplatesLoading(true));
      const accountsResp = await callApi(
        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/`
      );
      if (!request.isCurrent()) return;
      const { count, results } = accountsResp?.data ?? {};
      if (count === 0) {
        request.commit(slice.actions.setWhatsappTemplates([]));
        return;
      }
      const allTemplates = await Promise.all(
        getConnectedWhatsappBusinessAccounts(results).map(async (account) => {
          if (!request.isCurrent()) return [];
          const templateResp = await callApi(
            `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${account.id}/whatsapp-message-templates/?ordering=-created_at`
          );
          return (templateResp?.data?.results || []).map((t) => ({
            ...t,
            businessName: account.name,
            businessId: account.id,
          }));
        }) || []
      );
      // Strip Personal Reply templates — reserved for the closed-window composer,
      // never shown in the campaign builder's template list.
      request.commit(
        slice.actions.setWhatsappTemplates(
          allTemplates.flat().filter((t) => !isQuickReplyTemplate(t))
        )
      );
    } catch (error) {
      request.commit(slice.actions.setWhatsappTemplates([]));
      request.commit(updateError({ success: false, message: 'Failed to load WhatsApp templates' }));
    } finally {
      // Guarantee the spinner clears even on the early count===0 return or a throw.
      request.commit(slice.actions.setWhatsappTemplatesLoading(false));
    }
  };
}

export function fetchWhatsappPhones(practiceId) {
  return async () => {
    if (!practiceId) return;
    if (!hasFeatureAccess('whatsapp-messaging')) return;
    const request = captureBuilderRequest(practiceId);
    if (!request.isCurrent()) return;
    try {
      request.commit(slice.actions.setWhatsappPhonesLoading(true));
      const accountsResp = await callApi(
        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/`
      );
      if (!request.isCurrent()) return;
      const { results } = accountsResp?.data ?? {};
      const allPhones = await Promise.all(
        getConnectedWhatsappBusinessAccounts(results).map(async (account) => {
          if (!request.isCurrent()) return [];
          const phonesResp = await callApi(
            `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${account.id}/whatsapp-phones/`
          );
          return (phonesResp?.data?.results || []).map((p) => ({
            ...p,
            businessName: account.name,
            businessAccountId: account.id,
          }));
        })
      );
      request.commit(slice.actions.setWhatsappPhones(allPhones.flat()));
    } catch (error) {
      request.commit(slice.actions.setWhatsappPhones([]));
      request.commit(updateError({ success: false, message: 'Failed to load WhatsApp phones' }));
    } finally {
      // Guarantee the spinner clears even on an early throw.
      request.commit(slice.actions.setWhatsappPhonesLoading(false));
    }
  };
}

export function fetchRecipientCount(practiceId, filters, options = {}) {
  return async () => {
    if (!practiceId || !filters) return;
    const request = captureBuilderRequest(practiceId);
    if (!request.isCurrent()) return;
    try {
      request.commit(slice.actions.setRecipientCountLoading(true));
      // Use the campaign-less audiences "preview-count" endpoint. The previous
      // call hit /campaigns/0/preview_recepients/, but that action is detail=True
      // (needs a saved campaign) so pk=0 always 404'd → the recipient count never
      // loaded. preview-count takes the same filter shape, returns {count}, and
      // respects is_open_for_communication. is_archived_included reflects the
      // "Include archive" toggle so archived (still-contactable) leads are counted.
      const response = await callApi(
        `/api/v1/practices/${practiceId}/audiences/preview-count/`,
        'post',
        {
          filters,
          is_archived_included: !!options.isArchivedIncluded,
          is_only_archived: !!options.isOnlyArchived,
        }
      );
      if (response.status === 200) {
        request.commit(slice.actions.setRecipientCount(response.data?.count ?? 0));
      } else {
        request.commit(slice.actions.setRecipientCount(null));
      }
    } catch (error) {
      // Intentionally silent: a failed preview-count is non-critical — fall back
      // to a null count (renders as "—") rather than blocking the builder with an
      // error banner. setRecipientCount(null) also clears the loading flag below.
      request.commit(slice.actions.setRecipientCount(null));
    } finally {
      // Guarantee the spinner clears even on an early throw.
      request.commit(slice.actions.setRecipientCountLoading(false));
    }
  };
}

// Engagement follow-up: rule counts + tracking caveat for a source campaign.
// Used by both EngagementAudienceDialog (report entry + builder "Change") and
// CampaignReportPage (to gate the "Create follow-up" button on `eligible`).
export function fetchEngagementAnalysis(practiceId, sourceCampaignId) {
  return async () => {
    if (!practiceId || !sourceCampaignId) return;
    const request = captureBuilderRequest(practiceId);
    if (!request.isCurrent()) return;
    try {
      request.commit(slice.actions.setEngagementAnalysisLoading(true));
      const response = await callApi(
        `/api/v1/practices/${practiceId}/campaigns/${sourceCampaignId}/engagement-audiences/`
      );
      if (response.status === 200) {
        request.commit(slice.actions.setEngagementAnalysis({ data: response.data, campaignId: sourceCampaignId }));
      } else {
        // Distinguish "couldn't load" from "zero eligible recipients" — a
        // failed fetch must not be read by callers as an analysis that
        // legitimately resolved to zero.
        request.commit(slice.actions.setEngagementAnalysisError({ campaignId: sourceCampaignId }));
      }
    } catch (error) {
      request.commit(slice.actions.setEngagementAnalysisError({ campaignId: sourceCampaignId }));
    } finally {
      request.commit(slice.actions.setEngagementAnalysisLoading(false));
    }
  };
}

// Engagement follow-up: live paginated preview of the leads a rule/exclude
// combination currently resolves to. The count on the response also drives
// the audience-step live count and the review-step recipient estimate.
export function fetchEngagementPreview(practiceId, options = {}) {
  const { sourceCampaignId, rule, excludeBooked, page } = options;
  return async () => {
    if (!practiceId || !sourceCampaignId || !rule) return;
    const request = captureBuilderRequest(practiceId);
    if (!request.isCurrent()) return;
    try {
      request.commit(slice.actions.setEngagementPreviewLoading(true));
      const response = await callApi(
        `/api/v1/practices/${practiceId}/campaigns/${sourceCampaignId}/engagement-audiences/preview/`,
        'post',
        {
          rule,
          exclude_booked: !!excludeBooked,
          ...(page ? { page } : {}),
        }
      );
      if (response.status === 200) {
        request.commit(slice.actions.setEngagementPreview(response.data));
      } else {
        request.commit(slice.actions.setEngagementPreview(null));
      }
    } catch (error) {
      request.commit(slice.actions.setEngagementPreview(null));
    } finally {
      request.commit(slice.actions.setEngagementPreviewLoading(false));
    }
  };
}

export function fetchSequenceTemplatesForBuilder(practiceId) {
  return async () => {
    if (!practiceId) return null;
    const request = captureBuilderRequest(practiceId);
    if (!request.isCurrent()) return null;
    try {
      request.commit(slice.actions.setSequenceTemplatesLoading(true));
      const response = await callApi(`/api/v1/practices/${practiceId}/campaign-sequences/templates/`);
      if (!request.isCurrent()) return null;
      if (response.ok) {
        request.commit(slice.actions.setSequenceTemplates(response.data?.results || response.data || []));
      }
      return response.ok;
    } catch (error) {
      request.commit(updateError({ success: false, message: 'Failed to load sequence templates' }));
      return request.isCurrent() ? false : null;
    } finally {
      request.commit(slice.actions.setSequenceTemplatesLoading(false));
    }
  };
}

export function applySequenceTemplateToBuilder(practiceId, templateId, fallbackChannel) {
  return async () => {
    if (!practiceId || !templateId) return null;
    const request = captureBuilderRequest(practiceId);
    if (!request.isCurrent()) return null;
    try {
      const response = await callApi(
        `/api/v1/practices/${practiceId}/campaign-sequences/${templateId}/apply-template/`,
        'post',
        { name: 'New from template' }
      );
      if (!request.isCurrent()) return null;
      if (!response.ok) return false;

      const sequence = response.data;
      const messagesResponse = await callApi(
        `/api/v1/practices/${practiceId}/campaign-sequences/${sequence.id}/messages/`
      );
      if (!request.isCurrent()) return null;
      if (!messagesResponse.ok) return false;

      const messages = (messagesResponse.data?.results || messagesResponse.data || []).map((message, index) => ({
        localId: `msg_${Date.now()}_${index}`,
        serverId: message.id,
        subject: message.subject || '',
        body: message.body || '',
        channel: message.channel || fallbackChannel || 'EMAIL',
        delay: message.delay,
        use_unlayer: message.use_unlayer !== false,
        unlayer_template: message.unlayer_template || {},
        unlayer_html: message.unlayer_html || '',
        is_enabled: message.is_enabled !== false,
        whatsapp_template: whatsappTemplateIdFromStep(message) || null,
      }));
      request.commit(slice.actions.updateCampaignData({
        sequenceMessages: messages,
        practiceSequenceId: sequence.id,
      }));
      return true;
    } catch (error) {
      request.commit(updateError({ success: false, message: 'Failed to apply template' }));
      return request.isCurrent() ? false : null;
    }
  };
}

export function createCampaignFromBuilder(practiceId, builderData, navigate, onSettled) {
  return async () => {
    if (!practiceId) return;
    const request = captureBuilderRequest(practiceId);
    if (!request.isCurrent()) return;
    try {
      request.commit(slice.actions.setIsSubmitting(true));
      request.commit(slice.actions.setSubmitError(null));

      const payload = {
        practiceId,
        name: builderData.name,
        channel: builderData.channel,
      };

      if (builderData.sendNow) {
        payload.scheduled_at = new Date(Date.now() + 30 * 1000).toISOString();
      } else if (builderData.scheduledAt) {
        payload.scheduled_at = builderData.scheduledAt;
      }

      payload.campaign_type = builderData.campaignType || 'STANDARD';

      if (builderData.channel === 'WHATSAPP') {
        payload.whatsapp_template = builderData.whatsappTemplateId;
        payload.whatsapp_phone = builderData.whatsappPhoneId;
      } else {
        payload.engagement_template = builderData.templateId;
      }

      // Engagement follow-up source takes over recipient selection entirely —
      // the backend accepts engagement_source only when audience/recepient are
      // both omitted, so a follow-up draft never carries a stale static
      // audience/filter payload alongside its live source.
      if (builderData.engagementSource) {
        payload.engagement_source = {
          source_campaign: builderData.engagementSource.sourceCampaignId,
          rule: builderData.engagementSource.rule,
          exclude_booked: !!builderData.engagementSource.excludeBooked,
        };
      } else {
        if (builderData.audienceId) {
          payload.audience = { audience: builderData.audienceId };
        }

        if (builderData.recipientFilters) {
          payload.recepient = {
            filters: builderData.recipientFilters,
            // Include archived (still-contactable) leads when the operator ticked
            // "Include archive" — matches the recipient count shown in the builder.
            is_archived_included: !!builderData.recipientArchiveIncluded,
          };
        }
      }

      // NURTURE flow: create sequence + messages, then attach to campaign
      if (builderData.campaignType === 'NURTURE') {
        const seqResp = await callApi(
          `/api/v1/practices/${practiceId}/campaign-sequences/`,
          'post',
          { name: `${builderData.name} Sequence`, description: '' }
        );
        if (!request.isCurrent()) return;
        if (seqResp.status !== 201) {
          throw new Error('Failed to create sequence');
        }
        const sequenceId = seqResp.data.id;
        request.commit(slice.actions.updateCampaignData({ practiceSequenceId: sequenceId }));

        // Create messages sequentially (order matters for delay sequence)
        const createMsg = async (msgs, idx) => {
          if (idx >= msgs.length) return undefined;
          const msg = msgs[idx];
          const msgResp = await callApi(
            `/api/v1/practices/${practiceId}/campaign-sequences/${sequenceId}/messages/`,
            'post',
            {
              subject: msg.subject,
              body: msg.body || '',
              channel: msg.channel || builderData.channel,
              delay: msg.delay,
              is_enabled: msg.is_enabled !== false,
              whatsapp_template: whatsappTemplateIdFromStep(msg) || null,
              use_unlayer: msg.use_unlayer !== false,
              unlayer_template: msg.unlayer_template || {},
              unlayer_html: msg.unlayer_html || '',
            }
          );
          if (!request.isCurrent()) return undefined;
          if (msgResp.status !== 201) {
            throw new Error(`Failed to create message: ${msg.subject}`);
          }
          return createMsg(msgs, idx + 1);
        };
        await createMsg(builderData.sequenceMessages, 0);
        if (!request.isCurrent()) return;

        payload.practice_sequence = sequenceId;
        payload.campaign_type = 'NURTURE';
        delete payload.engagement_template;
        delete payload.whatsapp_template;
      }

      const response = await callApi(
        `/api/v1/practices/${practiceId}/campaigns/`,
        'post',
        payload
      );
      if (!request.isCurrent()) return;

      if (response.status === 201) {
        const campaignId = response.data?.id;

        if (builderData.sendNow && campaignId) {
          const scheduleResp = await callApi(
            `/api/v1/practices/${practiceId}/campaigns/${campaignId}/schedule/`,
            'post'
          );
          if (!request.isCurrent()) return;
          if (scheduleResp.status >= 400) {
            request.commit(slice.actions.setSubmitError(
              `Campaign created but failed to schedule: ${JSON.stringify(scheduleResp.data) || 'Unknown error'}`
            ));
            request.commit(slice.actions.setIsSubmitting(false));
            if (request.isCurrent() && onSettled) onSettled();
            return;
          }
        }

        request.commit(slice.actions.setLaunchedCampaignId(campaignId));
        request.commit(slice.actions.setSubmitSuccess(true));
        request.commit(updateError({ success: true, message: 'Campaign created successfully' }));
      } else {
        request.commit(
          slice.actions.setSubmitError(
            JSON.stringify(response.data) || 'Failed to create campaign'
          )
        );
      }
    } catch (error) {
      request.commit(
        slice.actions.setSubmitError(error.message || 'Failed to create campaign')
      );
    } finally {
      request.commit(slice.actions.setIsSubmitting(false));
      if (request.isCurrent() && onSettled) onSettled();
    }
  };
}

export function updateCampaignFromBuilder(practiceId, campaignId, builderData, onSettled) {
  return async () => {
    if (!practiceId || !campaignId) return;
    const request = captureBuilderRequest(practiceId);
    if (!request.isCurrent()) return;
    try {
      request.commit(slice.actions.setIsSubmitting(true));
      request.commit(slice.actions.setSubmitError(null));

      const payload = {
        practiceId,
        name: builderData.name,
        channel: builderData.channel,
      };

      if (builderData.sendNow) {
        payload.scheduled_at = new Date(Date.now() + 30 * 1000).toISOString();
      } else if (builderData.scheduledAt) {
        payload.scheduled_at = builderData.scheduledAt;
      }

      payload.campaign_type = builderData.campaignType || 'STANDARD';

      if (builderData.channel === 'WHATSAPP') {
        payload.whatsapp_template = builderData.whatsappTemplateId;
        payload.whatsapp_phone = builderData.whatsappPhoneId;
      } else {
        payload.engagement_template = builderData.templateId;
      }

      // Engagement follow-up source takes over recipient selection entirely —
      // omit audience/recepient (the backend rejects engagement_source
      // alongside either) so editing a follow-up draft can't silently revert
      // to a stale static audience.
      if (builderData.engagementSource) {
        payload.engagement_source = {
          source_campaign: builderData.engagementSource.sourceCampaignId,
          rule: builderData.engagementSource.rule,
          exclude_booked: !!builderData.engagementSource.excludeBooked,
        };
      } else {
        if (builderData.audienceId) {
          payload.audience = { audience: builderData.audienceId };
          payload.recepient = null;
        }

        if (builderData.recipientFilters) {
          payload.recepient = {
            filters: builderData.recipientFilters,
            // Include archived (still-contactable) leads when the operator ticked
            // "Include archive" — matches the recipient count shown in the builder.
            is_archived_included: !!builderData.recipientArchiveIncluded,
          };
          payload.audience = null;
        }
      }

      if (builderData.campaignType === 'NURTURE') {
        delete payload.whatsapp_template;
      }

      const response = await callApi(
        `/api/v1/practices/${practiceId}/campaigns/${campaignId}/`,
        'put',
        payload
      );
      if (!request.isCurrent()) return;

      if (response.status === 200) {
        // Handle sequence messages for NURTURE campaigns
        if (builderData.campaignType === 'NURTURE' && builderData.practiceSequenceId) {
          const seqId = builderData.practiceSequenceId;
          const builderMessages = builderData.sequenceMessages || [];

          // Fetch current server messages
          const serverRes = await callApi(
            `/api/v1/practices/${practiceId}/campaign-sequences/${seqId}/messages/`
          );
          if (!request.isCurrent()) return;
          const serverMessages = serverRes.ok ? (serverRes.data?.results || serverRes.data || []) : [];
          const serverIds = new Set(serverMessages.map((m) => m.id));
          const builderServerIds = new Set(builderMessages.filter((m) => m.serverId).map((m) => m.serverId));

          // DELETE removed messages
          const deleteResults = await Promise.all(
            serverMessages
              .filter((msg) => !builderServerIds.has(msg.id))
              .map((msg) =>
                callApi(
                  `/api/v1/practices/${practiceId}/campaign-sequences/${seqId}/messages/${msg.id}/`,
                  'delete'
                )
              )
          );
          if (!request.isCurrent()) return;

          // CREATE new messages (no serverId)
          const createResults = await Promise.all(
            builderMessages
              .filter((msg) => !msg.serverId)
              .map((msg) =>
                callApi(
                  `/api/v1/practices/${practiceId}/campaign-sequences/${seqId}/messages/`,
                  'post',
                  {
                    subject: msg.subject,
                    body: msg.body,
                    channel: msg.channel,
                    delay: msg.delay,
                    use_unlayer: msg.use_unlayer || false,
                    unlayer_template: msg.unlayer_template,
                    unlayer_html: msg.unlayer_html,
                    is_enabled: msg.is_enabled !== false,
                    whatsapp_template: whatsappTemplateIdFromStep(msg) || null,
                  }
                )
              )
          );
          if (!request.isCurrent()) return;

          // PATCH modified messages
          const patchResults = await Promise.all(
            builderMessages
              .filter((msg) => msg.serverId && serverIds.has(msg.serverId))
              .map((msg) =>
                callApi(
                  `/api/v1/practices/${practiceId}/campaign-sequences/${seqId}/messages/${msg.serverId}/`,
                  'patch',
                  {
                    subject: msg.subject,
                    body: msg.body,
                    channel: msg.channel,
                    delay: msg.delay,
                    use_unlayer: msg.use_unlayer || false,
                    unlayer_template: msg.unlayer_template,
                    unlayer_html: msg.unlayer_html,
                    is_enabled: msg.is_enabled !== false,
                    whatsapp_template: whatsappTemplateIdFromStep(msg) || null,
                  }
                )
              )
          );
          if (!request.isCurrent()) return;

          // Check if any message operations failed
          const allResults = [...deleteResults, ...createResults, ...patchResults];
          const failedOps = allResults.filter((r) => r && !r.ok);
          if (failedOps.length > 0) {
            request.commit(slice.actions.setSubmitError(`Campaign saved but ${failedOps.length} message operation(s) failed`));
            request.commit(updateError({ success: false, message: `Campaign saved but ${failedOps.length} sequence message(s) failed to sync` }));
            return;
          }
        }

        // Fire the FSM transition DRAFT → SCHEDULED when the user picked
        // "Send Now" (or any future scheduled_at) on an EDIT. Without
        // this the PUT only writes the scheduled_at field but the
        // campaign stays in DRAFT and the Celery worker never picks it
        // up — mirror of the create-flow logic on line ~425.
        //
        // The backend FSM safely rejects the call if the campaign has
        // already moved past DRAFT (e.g. user re-saves an existing
        // SCHEDULED campaign). We swallow that error to avoid showing
        // a false "failed to schedule" message — the campaign is still
        // in a valid send state.
        const needsScheduleTransition = builderData.sendNow || builderData.scheduledAt;
        if (needsScheduleTransition) {
          const scheduleResp = await callApi(
            `/api/v1/practices/${practiceId}/campaigns/${campaignId}/schedule/`,
            'post'
          );
          if (!request.isCurrent()) return;
          // Only surface 4xx errors that aren't "already past DRAFT" —
          // a 400 with TransitionNotAllowed-style detail means the
          // campaign was already scheduled / sending / sent, which is
          // not a user-visible failure on save.
          if (scheduleResp.status >= 400) {
            const detail = JSON.stringify(scheduleResp.data || '');
            const isAlreadyScheduled = /transition|already.*(scheduled|sending|sent)|invalid.*state/i.test(detail);
            if (!isAlreadyScheduled) {
              request.commit(slice.actions.setSubmitError(
                `Campaign updated but failed to schedule: ${detail || 'Unknown error'}`
              ));
              request.commit(slice.actions.setIsSubmitting(false));
              if (request.isCurrent() && onSettled) onSettled();
              return;
            }
          }
        }

        request.commit(slice.actions.setLaunchedCampaignId(campaignId));
        request.commit(slice.actions.setSubmitSuccess(true));
        request.commit(updateError({ success: true, message: 'Campaign updated successfully' }));
      } else {
        request.commit(
          slice.actions.setSubmitError(
            JSON.stringify(response.data) || 'Failed to update campaign'
          )
        );
      }
    } catch (error) {
      request.commit(
        slice.actions.setSubmitError(error.message || 'Failed to update campaign')
      );
    } finally {
      request.commit(slice.actions.setIsSubmitting(false));
      if (request.isCurrent() && onSettled) onSettled();
    }
  };
}

export function fetchSequenceMessages(practiceId, sequenceId) {
  return async () => {
    if (!practiceId || !sequenceId) return;
    const request = captureBuilderRequest(practiceId);
    if (!request.isCurrent()) return;
    try {
      const response = await callApi(
        `/api/v1/practices/${practiceId}/campaign-sequences/${sequenceId}/`
      );
      if (response.status === 200 && response.data?.practice_messages) {
        const messages = response.data.practice_messages.map((pm) => ({
          localId: `srv_${pm.id}`,
          serverId: pm.id,
          subject: pm.subject || '',
          body: pm.body || '',
          delay: pm.delay || 0,
          channel: pm.channel || 'EMAIL',
          use_unlayer: pm.use_unlayer !== false,
          unlayer_template: pm.unlayer_template || {},
          unlayer_html: pm.unlayer_html || '',
          is_enabled: pm.is_enabled !== false,
          whatsapp_template: whatsappTemplateIdFromStep(pm) || null,
        }));
        request.commit(slice.actions.updateCampaignData({ sequenceMessages: messages }));
      }
    } catch (error) {
      request.commit(updateError({ success: false, message: 'Failed to load sequence messages' }));
    }
  };
}

export function loadCampaignForEdit(practiceId, campaignId) {
  return async () => {
    if (!practiceId || !campaignId) return;
    const request = captureBuilderRequest(practiceId);
    if (!request.isCurrent()) return;
    try {
      request.commit(slice.actions.setEditCampaignLoading(true));
      const response = await callApi(
        `/api/v1/practices/${practiceId}/campaigns/${campaignId}/`
      );
      if (response.status === 200) {
        request.commit(slice.actions.setEditCampaign(response.data));
        if (
          request.isCurrent()
          && response.data?.campaign_type === 'NURTURE'
          && response.data?.practice_sequence
        ) {
          await dispatch(fetchSequenceMessages(
            practiceId,
            response.data.practice_sequence?.id || response.data.practice_sequence
          ));
        }
      } else {
        request.commit(updateError({ success: false, message: 'Failed to load campaign' }));
      }
    } catch (error) {
      request.commit(updateError({ success: false, message: error.message || 'Failed to load campaign' }));
    } finally {
      // Guarantee the spinner clears even on an early throw. (setEditCampaign also
      // clears editCampaignLoading on the success path; this is the catch-all.)
      request.commit(slice.actions.setEditCampaignLoading(false));
    }
  };
}

// Send test email for STANDARD campaigns (engagement templates)
export function sendTestEmailForTemplate(practiceId, templateId, email) {
  return async () => {
    try {
      const response = await callApi(
        `/api/v1/practices/${practiceId}/engagement-templates/${templateId}/send_test/`,
        'post',
        { email }
      );
      if (response.status === 200) {
        dispatch(updateError({ success: true, message: 'Test email sent successfully' }));
        return true;
      }
      dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to send test email' }));
      return false;
    } catch (error) {
      dispatch(updateError({ success: false, message: error.message || 'Failed to send test email' }));
      return false;
    }
  };
}

export function fetchWhatsappLimits(practiceId, businessAccountId, phoneId) {
  return async () => {
    if (!practiceId || !businessAccountId || !phoneId) return;
    if (!hasFeatureAccess('whatsapp-messaging')) return;
    const request = captureBuilderRequest(practiceId);
    if (!request.isCurrent()) return;
    try {
      const response = await callApi(
        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessAccountId}/whatsapp-phones/${phoneId}/limits/`
      );
      if (response.ok) {
        request.commit(slice.actions.setWhatsappLimits(response.data));
      }
    } catch (error) {
      // Non-critical — silently fail
    }
  };
}

// Send test email for NURTURE campaigns (campaign sequence messages)
export function sendTestEmailForSequenceMessage(practiceId, sequenceId, messageId, email) {
  return async () => {
    try {
      const response = await callApi(
        `/api/v1/practices/${practiceId}/campaign-sequences/${sequenceId}/messages/${messageId}/send-test/`,
        'put',
        { email }
      );
      if (response.status === 200) {
        dispatch(updateError({ success: true, message: 'Test email sent successfully' }));
        return true;
      }
      dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to send test email' }));
      return false;
    } catch (error) {
      dispatch(updateError({ success: false, message: error.message || 'Failed to send test email' }));
      return false;
    }
  };
}
